var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const MAX_BACKOFF_MS = 60000;
const MIN_DELAY_MS = 500;
export function startLiveState(opts) {
    var _a;
    const baseUrl = opts.baseUrl.replace(/\/$/, "") + "/";
    const endpoint = baseUrl + String(opts.agendaId) + "/live-state";
    const pollMs = Math.max(1000, opts.pollMs | 0);
    const jitterMs = Math.max(0, opts.jitterMs | 0);
    let timer = null;
    let controller = null;
    let lastEtag = null;
    let backoffAttempt = 0;
    let disposed = false;
    let paused = false;
    function nextDelay() {
        if (backoffAttempt > 0) {
            return Math.min(MAX_BACKOFF_MS, 1000 * Math.pow(2, backoffAttempt - 1));
        }
        const jitter = jitterMs > 0 ? (Math.random() - 0.5) * jitterMs : 0;
        return Math.max(MIN_DELAY_MS, pollMs + jitter);
    }
    function scheduleNext(delayMs) {
        if (disposed || paused)
            return;
        if (timer !== null)
            clearTimeout(timer);
        timer = setTimeout(() => {
            void tick();
        }, delayMs);
    }
    function tick() {
        return __awaiter(this, void 0, void 0, function* () {
            if (disposed || paused)
                return;
            if (controller) {
                controller.abort();
            }
            const ctrl = new AbortController();
            controller = ctrl;
            try {
                const headers = { Accept: "application/json" };
                if (lastEtag)
                    headers["If-None-Match"] = lastEtag;
                const res = yield fetch(endpoint, {
                    method: "GET",
                    credentials: "same-origin",
                    headers,
                    signal: ctrl.signal,
                });
                if (ctrl.signal.aborted || disposed)
                    return;
                if (res.status === 304) {
                    backoffAttempt = 0;
                    scheduleNext(nextDelay());
                    return;
                }
                if (!res.ok) {
                    throw new Error("live-state http " + res.status);
                }
                const etag = res.headers.get("ETag");
                if (etag)
                    lastEtag = etag;
                const payload = (yield res.json());
                if (ctrl.signal.aborted || disposed)
                    return;
                backoffAttempt = 0;
                try {
                    opts.onUpdate(payload);
                }
                catch (err) {
                    if (opts.onError)
                        opts.onError(err);
                }
            }
            catch (err) {
                if (ctrl.signal.aborted || disposed)
                    return;
                backoffAttempt = Math.min(6, backoffAttempt + 1);
                if (opts.onError)
                    opts.onError(err);
            }
            finally {
                if (controller === ctrl)
                    controller = null;
            }
            scheduleNext(nextDelay());
        });
    }
    function pause() {
        if (paused)
            return;
        paused = true;
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        if (controller) {
            controller.abort();
            controller = null;
        }
    }
    function resume(immediate) {
        if (disposed || !paused)
            return;
        paused = false;
        scheduleNext(immediate ? 0 : nextDelay());
    }
    function onVis() {
        if (document.hidden)
            pause();
        else
            resume(true);
    }
    function onOnline() {
        resume(true);
    }
    function onOffline() {
        pause();
    }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // First tick spread: small random offset by default so the cascade lands
    // within ~hundreds of ms of page load. Admin opts into 0 for "fire now".
    const firstTickMax = Math.max(0, (_a = opts.firstTickMaxMs) !== null && _a !== void 0 ? _a : 500);
    scheduleNext(firstTickMax === 0 ? 0 : Math.random() * firstTickMax);
    return () => {
        disposed = true;
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        if (controller) {
            controller.abort();
            controller = null;
        }
        document.removeEventListener("visibilitychange", onVis);
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
    };
}
/**
 * Format a `[startMin, endMin]` range using a WP `time_format` string.
 *
 * Detects 12h vs 24h from the presence of `g`/`h`/`a`/`A` tokens. Casing of
 * AM/PM follows the runtime's default locale (en-US uppercases, most others
 * lowercase). This may diverge slightly from the server-rendered initial
 * string in 12h locales — acceptable for live updates.
 */
export function formatTimeRange(startMin, endMin, wpFormat) {
    const hour12 = /[ghaA]/.test(wpFormat);
    const opts = {
        hour: hour12 ? "numeric" : "2-digit",
        minute: "2-digit",
        hour12,
    };
    const startStr = formatSingleMinute(startMin, opts);
    if (endMin === null || endMin <= startMin)
        return startStr;
    return startStr + " - " + formatSingleMinute(endMin, opts);
}
function formatSingleMinute(min, opts) {
    const total = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return new Intl.DateTimeFormat(undefined, opts).format(d);
}
